/**
 * DropZone - Handles drag & drop file upload
 * Triggers callback when audio file is dropped
 */
export class DropZone {
  private element: HTMLElement;
  private onFileDrop: (file: File) => void;
  private isActive = false;

  constructor(elementId: string, onFileDrop: (file: File) => void) {
    const el = document.getElementById(elementId);
    if (!el) {
      throw new Error(`Element with id "${elementId}" not found`);
    }

    this.element = el;
    this.onFileDrop = onFileDrop;
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Prevent default drag behaviors
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach((eventName) => {
      this.element.addEventListener(eventName, this.preventDefaults, false);
      document.body.addEventListener(eventName, this.preventDefaults, false);
    });

    // Highlight drop zone when dragging over
    ['dragenter', 'dragover'].forEach((eventName) => {
      this.element.addEventListener(eventName, () => this.highlight(), false);
    });

    ['dragleave', 'drop'].forEach((eventName) => {
      this.element.addEventListener(eventName, () => this.unhighlight(), false);
    });

    // Handle dropped files
    this.element.addEventListener('drop', (e) => this.handleDrop(e), false);

    // Also support click to upload - only on drop-overlay or drop-zone-content, not toolbar
    this.element.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      // Only trigger file input if clicking on drop overlay or its children
      const dropOverlay = this.element.querySelector('.drop-overlay');
      if (dropOverlay && (dropOverlay === target || dropOverlay.contains(target))) {
        this.triggerFileInput();
      }
    }, false);
  }

  private preventDefaults(e: Event): void {
    e.preventDefault();
    e.stopPropagation();
  }

  private highlight(): void {
    if (!this.isActive) {
      this.element.classList.add('drag-active');
      this.isActive = true;
    }
  }

  private unhighlight(): void {
    this.element.classList.remove('drag-active');
    this.isActive = false;
  }

  private async handleDrop(e: DragEvent): Promise<void> {
    const dt = e.dataTransfer;
    if (!dt) return;

    const files = dt.files;
    if (files.length === 0) return;

    const file = files[0];

    // Validate audio file
    if (!this.isAudioFile(file)) {
      console.error('❌ Invalid file type. Please drop an audio file.');
      alert('Please drop an audio file (mp3, wav, ogg, m4a, etc.)');
      return;
    }

    await this.onFileDrop(file);
  }

  private isAudioFile(file: File): boolean {
    const audioTypes = [
      'audio/mpeg',
      'audio/mp3',
      'audio/wav',
      'audio/wave',
      'audio/x-wav',
      'audio/ogg',
      'audio/webm',
      'audio/flac',
      'audio/aac',
      'audio/m4a',
      'audio/mp4',
    ];

    return audioTypes.includes(file.type) || file.name.match(/\.(mp3|wav|ogg|m4a|flac|aac)$/i) !== null;
  }

  private triggerFileInput(): void {
    // Create hidden file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'audio/*';
    input.style.display = 'none';

    input.onchange = async (e) => {
      const target = e.target as HTMLInputElement;
      const files = target.files;
      if (files && files.length > 0) {
        const file = files[0];
        await this.onFileDrop(file);
      }
    };

    document.body.appendChild(input);
    input.click();
    setTimeout(() => document.body.removeChild(input), 1000);
  }

  /**
   * Update drop zone text
   */
  updateText(text: string): void {
    const textEl = this.element.querySelector('.drop-zone-text');
    if (textEl) {
      textEl.textContent = text;
    }
  }

  /**
   * Set loading state
   */
  setLoading(isLoading: boolean): void {
    if (isLoading) {
      this.element.classList.add('loading');
      this.updateText('Loading...');
    } else {
      this.element.classList.remove('loading');
    }
  }
}
